import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useSearchParams } from '@uhg-abyss/web/tools/reactRouterTools';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Box } from '@uhg-abyss/web/ui/Box';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Link } from '@uhg-abyss/web/ui/Link';
import { PageBody } from '@uhg-abyss/web/ui/PageBody';
import { Text } from '@uhg-abyss/web/ui/Text';
import { useFlags } from 'launchdarkly-react-client-sdk';
import find from 'lodash/find';
import { useGuestStore } from 'parcels/src/store/useGuestStore';
import { GuestState } from 'parcels/src/store/useGuestStore/guestStore';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../common/AdobeTagging/adobeLinkTrackEvent';
import {
  Constants,
  LineOfBusiness,
  ReverseCoverageTypesCodes,
} from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { StyledDiv } from '../../common/ConstantsStyles';
import { LawwSignInAlert } from '../../common/GuestHeader/LawwSignInAlert';
import { setGeoLocationToStorage } from '../../common/PSXHeader/SearchBar/utils';
import { PSXIcon } from '../../common/PSXIcon';
import { useAdobePageTrackEvent } from '../../hooks/adobeHook/useAdobePageTrackEvent';
import { useSaveEducationalContentStatus } from '../../hooks/useEducationalContent';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { useGuestAcquisitionStore } from '../../store/useGuestAcquisitionStore';
import { GuestAcquisitionState } from '../../store/useGuestAcquisitionStore/guestAcquisitionStore';
import { FirstTimeUserBanner } from './common/FirstTimeUserBanner';
import GuestCoachmark from './common/GuestCoachmark';
import { InformationalDrawer } from './common/InformationalDrawer';
import { toSlug } from './Guest.utils';
import { GuestFirstTimeUserModal } from './GuestFirstTimeUserModal';
import { getFilteredGuestLobOptions } from './GuestLineOfBusinessPicker.utils';
import { InfoButtonStyle, StyleForHeader } from './GuestPlanSelection.style';
import {
  getBlueGreenGQLApiUrl,
  isLawwGuest,
  isYearFirstFlowEligibleForGuest,
} from './utils';

const LineOfBusinessStyledCard = styled(Card, {
  '&.abyss-card-root': {
    width: '180px',
    height: '343px',
    borderRadius: '8px',
    padding: '0px',
    marginTop: '0px !important',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    },
    '.abyss-link-root': {
      alignItems: 'center',
      display: 'inline-flex',
    },
  },
});

type LineOfBusinessCardProps = {
  title: string;
  analyticsTitle: string;
  description: string;
  icon: string;
  linkText: string;
  lob: string;
};

const LineOfBusinessCard = ({
  title,
  analyticsTitle,
  description,
  icon,
  linkText,
  lob,
}: LineOfBusinessCardProps) => {
  const { i18n } = useTranslation();
  const { navigate } = useRouter();
  let url = '/select-coverage-type?planSelectionLob=' + lob;
  const featureFlags = useLagoon('feature-flags')();
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { chipValue } = chipStore;
  const coverageLinks = useLagoon('coverage-links')();

  const { guestPlanSelectorData, setGuestPlanSelectorData } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestPlanSelectorData: state.guestPlanSelectorData,
      setGuestPlanSelectorData: state.setGuestPlanSelectorData,
    }))
  );

  const { setIsNavigatingMaestro, clearRequestData } = useGuestAcquisitionStore(
    useShallow((state: GuestAcquisitionState) => ({
      setIsNavigatingMaestro: state.setIsNavigatingMaestro,
      clearRequestData: state.clearRequestData,
    }))
  );
  const isLawwGuestExperience = isLawwGuest();
  const { coverageType, isGuestDeeplinkFlow } = guestPlanSelectorData || {};
  const isyearFirstFlowEligibleForLAWW = isLawwGuestExperience
    ? isYearFirstFlowEligibleForGuest(lob, 'B', featureFlags)
    : false;
  const cardWidth = i18n.language?.toLowerCase().startsWith('es')
    ? '189px'
    : '180px';

  const handleClick = () => {
    setGeoLocationToStorage({});
    sessionStorage.removeItem(Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION);
    setIsNavigatingMaestro(false);
    clearRequestData();
    adobeLinkTrackEvent({
      name: linkText,
      location: analyticsTitle,
      type: 'internal',
      destinationUrl: url,
    });

    const isYearFirstEligible = isYearFirstFlowEligibleForGuest(
      lob,
      coverageType || '',
      featureFlags
    );

    if (isGuestDeeplinkFlow && coverageType) {
      const matchedCoverageLink = coverageLinks.find(
        (item) => item?.coverage === coverageType && item?.lob === lob
      )?.link;

      if (matchedCoverageLink) {
        window.open(matchedCoverageLink, '_blank');
        return;
      }

      // Special case: CNS with VISION coverage does not change the URL (business requirement)
      url =
        lob === LineOfBusiness.CNS &&
        coverageType === ReverseCoverageTypesCodes.VISION
          ? url
          : !isYearFirstEligible
          ? `/plan-selection?planSelectionLob=${lob}&coverageType=${coverageType}&chipValue=${chipValue}`
          : `/plan-year-selection?planSelectionLob=${lob}&coverageType=${coverageType}`;
    }

    if (isLawwGuestExperience && !isyearFirstFlowEligibleForLAWW) {
      url = `/plan-selection?planSelectionLob=${lob}&coverageType=B&chipValue=Behavioral%20Health`;
    } else if (isLawwGuestExperience && isyearFirstFlowEligibleForLAWW) {
      url = `/plan-year-selection?planSelectionLob=${lob}&coverageType=B&chipValue=Behavioral%20Health`;
    }
      // Non-LAWW deeplinks with a known coverage type must use their own eligibility result,
    // not the LAWW-only flag, so the breadcrumb order matches the route we just built above.
    const shouldUseDeeplinkEligibility = isGuestDeeplinkFlow && coverageType;
    const deeplinkEligibility = shouldUseDeeplinkEligibility
      ? isYearFirstEligible
      : false;
    const resolvedIsYearFirstFlow = isLawwGuestExperience
      ? isyearFirstFlowEligibleForLAWW
      : deeplinkEligibility;
    setGuestPlanSelectorData({
      ...guestPlanSelectorData,
      isYearFirstFlow: resolvedIsYearFirstFlow,
    });
    navigate(url);
  };

  return (
    <LineOfBusinessStyledCard onClick={handleClick}>
      <StyledDiv
        css={{
          padding: '0px',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Layout.Stack
          css={{
            'abyss-layout-stack': {
              padding: '16px',
              paddingBottom: '2px',
              alignItems: 'start',
            },
          }}
        >
          {/* Note: The health_access icon is not available in Abyss for Optum branding hence created a custom svg */}
          {icon === 'health_access_optum_custom_svg' ? (
            <PSXIcon icon="health_access_optum_custom_svg" />
          ) : (
            <IconBrand icon={icon} size="48px" />
          )}
          <Heading level={6} size="xs">
            {title}
          </Heading>
          <Text
            asElement="p"
            css={{
              display: 'inline-block',
              lineHeight: '1.3',
            }}
            fontSize="sm"
          >
            {description}
          </Text>
        </Layout.Stack>
      </StyledDiv>
      <StyledDiv
        css={{
          padding: '16px',
          height: '52px',
          width: cardWidth,
          marginLeft: '0px',
          marginRight: '0px',
          borderTop: '1px solid $greyBorderColor',
        }}
      >
        <Layout.Group>
          <Link
            afterIcon="chevron_right"
            aria-label={`${linkText} ${title}`}
            data-testid={`explore-${toSlug(lob || '')}-link`}
            isStandardAnchor
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            size={'$sm'}
          >
            {linkText}
          </Link>
        </Layout.Group>
      </StyledDiv>
    </LineOfBusinessStyledCard>
  );
};

export const GuestLineOfBusinessPicker = () => {
  const { t } = useTranslation();
  const { chooseType, guestData, guestPlanSelectorData, setChooseType } =
    useGuestStore(
      useShallow((state: GuestState) => ({
        chooseType: state.chooseType,
        guestData: state.guestData,
        guestPlanSelectorData: state.guestPlanSelectorData,
        setChooseType: state.setChooseType,
      }))
    );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const lobs = useLagoon('lob')();
  const { coverageType, isGuestDeeplinkFlow } = guestPlanSelectorData || {};

  const [
    isFirstTimeUserBannerEnabled,
    enableUpdatedPSXBannerToSignIn,
    isDisableStandaloneMedicaid,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.GUEST_FIRST_TIME_USER_BANNERS_ENABLED,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_UPDATED_PSX_BANNER_TO_SIGN_IN,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_STAND_ALONE_MEDICAID,
  ]);

  const isLawwGuestExperience = isLawwGuest();
  lobs?.sort((lob) => lob.displayOrder);

  const filteredLobs = getFilteredGuestLobOptions({
    lobs,
    isGuestDeeplinkFlow:
      Boolean(isGuestDeeplinkFlow) && isDisableStandaloneMedicaid,
    coverageType,
    lineOfBusiness: guestData?.lob,
  });

  const lagoonUIMessage = useLagoon('ui-messaging')();

  const chooseMedicalCoverageType = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CHOOSE_MEDICAL_COVERAGE_TYPE,
  })?.message;
  const typeOfMedicalCoverageHeadingText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.TYPE_OF_MEDICAL_COVERAGE_HEADING,
  })?.message;
  const getMoreCoverageTypesInfoText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.GET_MORE_COVERAGE_TYPE_INFO,
  })?.message;
  const getMoreCoverageTypesInfoButtonText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION
      .GET_MORE_COVERAGE_TYPE_INFO_BUTTON_TEXT,
  })?.message;
  const exploreCoverageText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.EXPLORE_COVERAGE,
  })?.message;

  const typeOfMedicalCoverageText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.TYPE_OF_MEDICAL_COVERAGE,
  })?.message;

  const coachmarkContentText =
    find(lagoonUIMessage, {
      key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOB_PICKER_COACHMARK_CONTENT,
    })?.message || '';

  const learnMoreButtonText =
    find(lagoonUIMessage, {
      key: ConstantsLagoon.GUEST_PLAN_SELECTION.LEARN_MORE_BUTTON_TEXT,
    })?.message || '';

  const bannerContentText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION
      .LOB_PICKER_FIRST_TIME_USER_BANNER_TEXT,
  })?.message;

  const signInMessageText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SIGN_IN_MESSAGE,
  })?.message;
  const signInText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SIGN_IN,
  })?.message?.replace(/\.$/, '');
  // Retrieve the sign-in URL saved by GuestHeader
  const signInUrl = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.GUEST_SIGN_IN_URL
  );

  const drawerHeaderText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOB_PICKER_DRAWER_HEADER,
  })?.message;

  const drawerContentText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOB_PICKER_DRAWER_CONTENT,
  })?.message;
  const drawerHeaderCoverageInfoText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION
      .COVERAGE_TYPE_INFORMATION_DRAWER_HEADER,
  })?.message;
  const drawerContentCoverageInfoText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION
      .COVERAGE_TYPE_INFORMATION_DRAWER_CONTENT,
  })?.message;
  const educationalContentStore = useStore(
    StoreKeys.EDUCATIONAL_CONTENT_STATUS
  );

  const setEducationalContentStore = useStore(
    StoreKeys.SET_EDUCATIONAL_CONTENT_STATUS
  );

  const flags = useFlags();
  const [searchParams] = useSearchParams();
  const guestBGApiUrl = getBlueGreenGQLApiUrl(flags, searchParams);

  const [, setEducationalContentStatus] = useSaveEducationalContentStatus({
    url: guestBGApiUrl,
  });

  const handleOnLearnMoreClick = () => {
    setIsDrawerOpen(true);
  };

  const handleOnClose = () => {
    setEducationalContentStore({
      ...educationalContentStore,
      hasViewedGuestLobBanner: true,
    });

    setEducationalContentStatus({
      variables: {
        ...educationalContentStore,
        hasViewedContent: educationalContentStore?.hasViewedContent ?? true,
        hasViewedGuestLobBanner: true,
      },
    });
  };

  useEffect(() => {
    if (chooseMedicalCoverageType) {
      setChooseType(chooseMedicalCoverageType);
    }
  }, [chooseMedicalCoverageType, setChooseType]);

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: `guest plan selection`,
    isGuest: true,
  });

  useEffect(() => {
    document.title = `Type of Coverage | ${Constants.SITE_NAME}`;
    adobePageTrackEvent();
  }, [adobePageTrackEvent]);

  const infoButtonAriaLabel = t(
    'MORE_INFORMATION_ABOUT_VARIOUS_MEDICAL_COVERAGES'
  );

  const handleInfoButtonFocus = (
    event: React.FocusEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    event.target.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const showFirstTimeUserBanner =
    isFirstTimeUserBannerEnabled &&
    educationalContentStore &&
    !educationalContentStore?.hasViewedGuestLobBanner;

  return (
    <>
      <GuestFirstTimeUserModal />
      <Box color="$bgHeaderColor" css={StyleForHeader}>
        <Flex css={{ width: '100%', justifyContent: 'center' }}>
          <Flex
            css={{ flexDirection: 'column', maxWidth: '1200px', width: '100%' }}
          >
            {typeOfMedicalCoverageText}
            <div style={{ position: 'relative' }}>
              <div
                id="coachmark-anchor"
                style={{ display: 'inline-block', maxWidth: '100%' }}
              >
                <Heading css={{ marginTop: '8px' }} level={2} size="lg">
                  {chooseType}
                </Heading>
              </div>
              {showFirstTimeUserBanner && enableUpdatedPSXBannerToSignIn && (
                <GuestCoachmark
                  content={coachmarkContentText}
                  learnMoreButtonText={learnMoreButtonText}
                  onClose={handleOnClose}
                  onLearnMore={handleOnLearnMoreClick}
                  position="right"
                  target="#coachmark-anchor"
                  title={chooseType}
                />
              )}
            </div>
          </Flex>
        </Flex>
      </Box>
      {showFirstTimeUserBanner && !enableUpdatedPSXBannerToSignIn && (
        <FirstTimeUserBanner
          content={bannerContentText}
          onClose={handleOnClose}
          onLearnMoreClick={handleOnLearnMoreClick}
          showLearnMoreButton={true}
        />
      )}
      {isLawwGuestExperience && <LawwSignInAlert />}
      <PageBody
        css={{
          marginBottom: '8px',
          padding: '32px 24px 24px 24px',
        }}
      >
        <Flex
          alignContent="center"
          css={{ gap: '6px' }}
          direction="row"
          justify="center"
        >
          <Heading
            data-testid="lob-selection"
            level={4}
            size="sm"
            textAlign="center"
          >
            {typeOfMedicalCoverageHeadingText}
          </Heading>
          {!isLawwGuestExperience && (
            <Button
              aria-label={infoButtonAriaLabel}
              css={InfoButtonStyle}
              data-testid="info-button"
              onClick={() => setIsDrawerOpen(true)}
              onFocus={handleInfoButtonFocus}
              style={{ alignSelf: 'center' }}
              variant="text"
            >
              <IconSymbol icon="info" size={16} variant="outlined" />
            </Button>
          )}
        </Flex>
        {isLawwGuestExperience && (
          <Flex
            alignItems="baseline"
            css={{ marginTop: '8px' }}
            direction="column"
            justify="center"
          >
            <Text css={{ alignSelf: 'center', textAlign: 'center' }}>
              {getMoreCoverageTypesInfoText}
            </Text>
            <Button
              aria-label={infoButtonAriaLabel}
              css={InfoButtonStyle}
              data-testid="info-button"
              onClick={() => setIsDrawerOpen(true)}
              onFocus={handleInfoButtonFocus}
              style={{ alignSelf: 'center' }}
              variant="text"
            >
              {getMoreCoverageTypesInfoButtonText}
            </Button>
          </Flex>
        )}

        {!isLawwGuestExperience && enableUpdatedPSXBannerToSignIn && (
          <Flex
            alignItems="baseline"
            css={{ marginTop: '8px' }}
            direction="row"
            justify="center"
          >
            <Text
              asElement="p"
              css={{ alignSelf: 'center', textAlign: 'center', margin: 0 }}
            >
              {signInMessageText}
            </Text>
            <Link
              data-testid="sign-in-link"
              href={signInUrl || ''}
              onClick={() => {
                adobeLinkTrackEvent({
                  name: signInText,
                  location: chooseType,
                  type: 'internal',
                  destinationUrl: signInUrl,
                });
              }}
              onFocus={handleInfoButtonFocus}
              openNewWindow={false}
              style={{ marginLeft: '4px', display: 'inline-flex' }}
            >
              {signInText}
              <IconSymbol icon="chevron_right" />
            </Link>
          </Flex>
        )}

        <Flex
          alignItems="baseline"
          direction="row"
          justify="center"
          style={{ gap: '24px', marginTop: '32px' }}
        >
          {filteredLobs.map((lob) => (
            <LineOfBusinessCard
              analyticsTitle={lob.dnt_name}
              description={lob.description}
              icon={lob.dntIcon}
              key={lob.key}
              linkText={exploreCoverageText}
              lob={lob.key}
              title={lob.displayName}
            />
          ))}
        </Flex>
        <InformationalDrawer
          content={
            isLawwGuestExperience
              ? drawerContentCoverageInfoText
              : drawerContentText
          }
          header={
            isLawwGuestExperience
              ? drawerHeaderCoverageInfoText
              : drawerHeaderText
          }
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
        />
      </PageBody>
    </>
  );
};
